import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, descargarRecurso } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Download, Upload, Sparkles, AlertTriangle, RefreshCw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchKoPendientes } from "@/lib/cobertura";
import { toast } from "sonner";

const TIPOS = ["politica", "procedimiento", "plan", "manual", "registro", "instruccion"];
const ESTADOS = ["borrador", "vigente", "obsoleto"];
const estadoColor = { borrador: "bg-[#F59E0B]", vigente: "bg-[#10B981]", obsoleto: "bg-slate-400" };

export default function Documentos() {
  const { user, scopeTenant } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusDoc = params.get("doc");
  const canEdit = useCan(user);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [docs, setDocs] = useState([]);
  const [completitud, setCompletitud] = useState(null);
  const [capitulos, setCapitulos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titulo: "", tipo: "procedimiento", codigo: "", fecha_revision: "", responsable: "", requisitos: [] });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (needsScope) return;
    const [d, n, c] = await Promise.all([api.get("/documentos"), api.get("/norma/requisitos"), api.get("/documentos/completitud")]);
    setDocs(d.data);
    setCapitulos(n.data.capitulos);
    setCompletitud(c.data);
  }, [needsScope]);

  useEffect(() => { load(); }, [load]);

  const toggleReq = (code) =>
    setForm((f) => ({
      ...f,
      requisitos: f.requisitos.includes(code) ? f.requisitos.filter((c) => c !== code) : [...f.requisitos, code],
    }));

  const submit = async () => {
    if (!file || !form.titulo) return toast.error("Título y fichero son obligatorios");
    setSaving(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("titulo", form.titulo);
    fd.append("tipo", form.tipo);
    fd.append("codigo", form.codigo);
    fd.append("fecha_revision", form.fecha_revision);
    fd.append("responsable", form.responsable);
    fd.append("requisitos", JSON.stringify(form.requisitos));
    try {
      await api.post("/documentos", fd);
      toast.success("Documento subido");
      setOpen(false);
      setForm({ titulo: "", tipo: "procedimiento", codigo: "", fecha_revision: "", responsable: "", requisitos: [] });
      setFile(null);
      load();
      const n = await fetchKoPendientes(api);
      if (n > 0) {
        toast(`Te quedan ${n} ${n === 1 ? "KO pendiente" : "KO pendientes"} de tu confirmación`, {
          description: "Revísalos y confírmalos en el Mapa Normativo.",
          action: { label: "Revisar", onClick: () => navigate("/norma") },
        });
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (doc, estado) => {
    const fd = new FormData();
    fd.append("estado", estado);
    try {
      await api.patch(`/documentos/${doc.id}/estado`, fd);
      toast.success(`Estado: ${estado}`);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const descargar = async (doc) => {
    try { await descargarRecurso("documento", doc.id); } catch { toast.error("No se pudo generar la descarga"); }
  };

  const [reanDlg, setReanDlg] = useState(null);
  const [reanBusy, setReanBusy] = useState(false);
  const reanalizarDryRun = async (doc) => {
    setReanBusy(true); setReanDlg({ doc, data: null });
    try {
      const { data } = await api.post(`/documentos/${doc.id}/reanalizar-dryrun`);
      setReanDlg({ doc, data });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setReanDlg(null); }
    finally { setReanBusy(false); }
  };
  const reanalizarAplicar = async () => {
    if (!reanDlg?.doc) return;
    setReanBusy(true);
    try {
      await api.post(`/documentos/${reanDlg.doc.id}/reanalizar-aplicar`);
      toast.success("Documento reanalizado y actualizado");
      setReanDlg(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReanBusy(false); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="documentos-page">
      <PageHeader
        overline="Nivel 1 · Documenta"
        title="Gestión Documental"
        action={canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/subir-ia")} data-testid="subir-ia-btn" className="border-[#FF5625] text-[#FF5625]">
              <Sparkles size={16} className="mr-1" /> Subir con IA
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nuevo-documento-btn" className="bg-[#FF5625] hover:bg-[#E0451A] active:scale-[0.98] transition-transform">
                <Plus size={16} className="mr-1" /> Nuevo documento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="documento-dialog">
              <DialogHeader><DialogTitle className="text-[#0E2A47]">Subir documento</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Título</Label>
                  <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    data-testid="doc-titulo" className="mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger data-testid="doc-tipo" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {TIPOS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Código</Label>
                    <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                      data-testid="doc-codigo" className="mt-1.5" placeholder="PR-01" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fecha de revisión</Label>
                  <DatePicker value={form.fecha_revision} onChange={(v) => setForm({ ...form, fecha_revision: v })} testid="doc-fecha" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Responsable</Label>
                  <Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })}
                    data-testid="doc-responsable" className="mt-1.5" placeholder="Responsable Calidad" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fichero</Label>
                  <Input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Requisitos IFS cubiertos</Label>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-[#E2E8F0] rounded-md p-3 space-y-2">
                    {capitulos.map((cap) =>
                      cap.requisitos.map((r) => (
                        <label key={r.codigo} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={form.requisitos.includes(r.codigo)} onCheckedChange={() => toggleReq(r.codigo)}
                            data-testid={`doc-req-${r.codigo}`} />
                          <span className="font-mono text-xs text-[#FF5625]">{r.codigo}</span>
                          <span className="text-[#0E2A47]/70 truncate">{r.texto}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="doc-submit"
                  className="bg-[#0E2A47] hover:bg-[#163C65]"><Upload size={15} className="mr-1" />
                  {saving ? "Subiendo…" : "Subir"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />

      {completitud && (
        <Card className="border-[#E2E8F0] p-5 mb-4" data-testid="completitud-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Calidad de las fichas</span>
              <p className="text-[11px] text-[#0E2A47]/45 normal-case font-normal">Mide los metadatos de los documentos (mapeo, responsable, revisión al día), no la cobertura de la norma.</p>
            </div>
            <span className="text-lg font-black tracking-tighter text-[#0E2A47]">{completitud.pct}%</span>
          </div>
          <div className="w-full h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all" style={{ width: `${completitud.pct}%` }} />
          </div>
          <div className="text-xs text-[#0E2A47]/50 mt-2" data-testid="completitud-desglose">
            {completitud.total} documento{completitud.total === 1 ? "" : "s"} · {completitud.desglose?.mapeados ?? 0} mapeado{(completitud.desglose?.mapeados) === 1 ? "" : "s"} · {completitud.desglose?.con_responsable ?? 0} con responsable · {completitud.desglose?.con_revision_vigente ?? 0} con revisión al día
            {completitud.completos > 0 && <> · <span className="text-[#10B981] font-semibold">{completitud.completos} al 100%</span></>}
          </div>
        </Card>
      )}

      <Card className="border-[#E2E8F0] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F7F8FA]">
              <TableHead>Código</TableHead><TableHead>Título</TableHead><TableHead>Tipo</TableHead>
              <TableHead>Versión</TableHead><TableHead>Estado</TableHead><TableHead>Requisitos</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-[#0E2A47]/50 py-8">Sin documentos.</TableCell></TableRow>
            )}
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`doc-row-${d.id}`} className={`hover:bg-[#F7F8FA] ${focusDoc === d.id ? "ring-2 ring-[#FF5625] ring-inset bg-[#FFF7F4]" : ""}`}>
                <TableCell className="font-mono text-xs">{d.codigo || "—"}</TableCell>
                <TableCell className="font-medium text-[#0E2A47]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{d.titulo}</span>
                    {d.idioma && d.idioma !== "es" && (
                      <span data-testid={`doc-idioma-${d.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#3730A3] border border-[#3730A3]/20 uppercase">{d.idioma}</span>
                    )}
                  </div>
                  {d.faltantes?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {d.faltantes.map((f) => (
                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF3EF] text-[#FF5625] border border-[#FF5625]/20">{f}</span>
                      ))}
                    </div>
                  )}
                  {focusDoc === d.id && (d.avisos_ia?.length > 0) && (
                    <div className="mt-2 rounded-lg border border-[#F59E0B]/50 bg-[#FFFBEB] p-2.5" data-testid={`doc-avisos-${d.id}`}>
                      <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#B45309] mb-1">
                        <AlertTriangle size={12} /> Avisos de la IA
                      </div>
                      <ul className="space-y-1">
                        {d.avisos_ia.map((a, i) => (
                          <li key={i} className="text-xs text-[#0E2A47]/75 flex gap-1.5">
                            {(typeof a !== "string" && a.numero)
                              ? <span className="shrink-0 font-mono font-semibold text-[#B45309]" title="Afecta a este requisito">{a.numero}</span>
                              : <span className="shrink-0 text-[#0E2A47]/40">General</span>}
                            <span>{typeof a === "string" ? a : (a.detalle || a.tipo)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="text-[11px] text-[#0E2A47]/50 mt-1">Los avisos con número afectan solo a ese requisito; corrige el documento para levantar esa reserva.</div>
                    </div>
                  )}
                </TableCell>
                <TableCell className="capitalize text-[#0E2A47]/70">{d.tipo}</TableCell>
                <TableCell>v{d.version}</TableCell>
                <TableCell>
                  {canEdit ? (
                    <Select value={d.estado} onValueChange={(v) => cambiarEstado(d, v)}>
                      <SelectTrigger data-testid={`doc-estado-${d.id}`} className="h-7 w-28 border-none px-2">
                        <Badge className={`${estadoColor[d.estado]} hover:${estadoColor[d.estado]} capitalize`}>{d.estado}</Badge>
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {ESTADOS.map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`${estadoColor[d.estado]} capitalize`}>{d.estado}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs font-mono text-[#0E2A47]/60">
                  {(d.requisitos || []).length === 0 ? "—" : (d.requisitos || []).map((n) => (
                    <span key={n} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 rounded ${(d.requisitos_retirados || []).includes(n) ? "bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]/30" : ""}`}>
                      {n}{(d.requisitos_retirados || []).includes(n) && " ⚠"}
                    </span>
                  ))}
                  {(d.requisitos_retirados || []).length > 0 && (
                    <div className="text-[10px] text-[#EF4444] mt-0.5">requisito retirado del catálogo</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => reanalizarDryRun(d)} disabled={reanBusy} data-testid={`doc-reanalizar-${d.id}`} title="Reanalizar con IA (dry-run)">
                      <RefreshCw size={15} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => descargar(d)} data-testid={`doc-descargar-${d.id}`}>
                    <Download size={15} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!reanDlg} onOpenChange={(o) => !o && setReanDlg(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="reanalizar-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Reanalizar con IA · vista previa</DialogTitle></DialogHeader>
          {!reanDlg?.data ? (
            <div className="py-8 text-center text-sm text-[#0E2A47]/50">Analizando el documento…</div>
          ) : reanDlg.data.error ? (
            <div className="py-6 text-sm text-[#EF4444]">No se pudo reanalizar: {reanDlg.data.error}</div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-[#0E2A47]/70">{reanDlg.data.titulo}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="rean-antes">
                  <div className="text-[10px] uppercase tracking-wider text-[#0E2A47]/40 mb-1">Antes</div>
                  <div>Requisitos: <b>{reanDlg.data.antes.n_requisitos}</b></div>
                  <div>Confianza media: <b>{reanDlg.data.antes.confianza_media}%</b></div>
                  <div>Idioma: <b>{reanDlg.data.antes.idioma || "—"}</b></div>
                </div>
                <div className="p-3 rounded-lg bg-[#FFF7ED] border border-[#FF5625]/30" data-testid="rean-despues">
                  <div className="text-[10px] uppercase tracking-wider text-[#FF5625] mb-1">Después</div>
                  <div>Requisitos: <b>{reanDlg.data.despues.n_requisitos}</b></div>
                  <div>Confianza media: <b>{reanDlg.data.despues.confianza_media}%</b></div>
                  <div>Idioma: <b>{reanDlg.data.despues.idioma || "—"}</b></div>
                </div>
              </div>
              {reanDlg.data.reset_ko && (
                <div className="text-[11px] text-[#B45309] bg-[#FEF3C7] rounded px-2 py-1.5 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />Se resetearán las confirmaciones KO anteriores de este documento (el mapeo cambia).
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReanDlg(null)} data-testid="rean-cancelar">Cancelar</Button>
            <Button className="bg-[#FF5625] hover:bg-[#E64A1E]" disabled={reanBusy || !reanDlg?.data || reanDlg?.data?.error} onClick={reanalizarAplicar} data-testid="rean-aplicar">
              {reanBusy ? "Aplicando…" : "Aplicar reanálisis"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
